import { useTranslation } from 'react-i18next';
import { Periode } from '../lib/model/kantoordagApi';
import { FaBuilding, FaCalendarDay, FaCar, FaParking, FaWindowClose } from 'react-icons/fa';
import { GiOfficeChair } from 'react-icons/gi';
import { ROOM__AUTOMATIC_ASSIGNMENT, ROOM__NO_ROOM } from '../app/Constants';
import { DateTime } from "luxon";
import { FaClock } from "react-icons/fa6";

/**
 * Shape shared by both calendar data sources on Reservations.tsx: the live
 * My Reservations listener (own user) and the REST upcoming-aanmeldingen
 * endpoint (admin viewing/booking for another user — see
 * specs/015-adopt-my-reservations-view/research.md Decision 3).
 */
export interface ReservationDetail {
    date: string;
    building: string;
    room: string;
    parkingRequested: boolean;
    parkingAssigned: boolean;
    periode: Periode;
}

interface ReservationDetailSlideInProps {
    isOpen: boolean;
    onClose: () => void;
    reservation: ReservationDetail | null;
}

export default function ReservationDetailSlideIn({ isOpen, onClose, reservation }: ReservationDetailSlideInProps) {
    const { t } = useTranslation("reservations");

    const slideInBaseClasses = `fixed bg-white shadow-lg z-50 p-4 overflow-y-auto transition-transform duration-300 ease-in-out transform`;
    const mobileClasses = `inset-x-0 bottom-0 h-1/2 ${isOpen ? 'translate-y-0' : 'translate-y-full'}`;
    const desktopClasses = `md:translate-y-0 md:inset-y-0 md:right-0 md:w-80 md:h-full md:left-auto ${isOpen ? 'md:translate-x-0' : 'md:translate-x-full'}`;

    return (
        <div className={`${slideInBaseClasses} ${mobileClasses} ${desktopClasses}`}>
            <div className="mb-4 p-3 border rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg mb-2">{t("reservation_details")}</h3>
                    <FaWindowClose color="red" onClick={onClose}>X</FaWindowClose>
                </div>
                {reservation && (
                    <div>
                        <div className="flex items-center mb-1">
                            <FaCalendarDay className="mr-2 text-gray-500"/>
                            <span>{DateTime.fromISO(reservation.date).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}</span>
                        </div>
                        <div className="flex items-center mb-1">
                            <FaBuilding className="mr-2 text-gray-500"/>
                            <span>{reservation.building}</span>
                        </div>
                        <div className="flex items-center mb-1">
                            <GiOfficeChair className="mr-2 text-gray-500"/>
                            <span>{reservation.room === ROOM__NO_ROOM ? t("no_room") :
                                reservation.room === ROOM__AUTOMATIC_ASSIGNMENT ? t("automatic_room") :
                                    reservation.room}</span>
                        </div>
                        {reservation.parkingRequested && !reservation.parkingAssigned && <div className="flex items-center mb-1">
                            <FaCar className="mr-2 text-gray-500"/>
                            <span>{t("parking_requested")}</span>
                        </div>
                        }
                        {reservation.parkingAssigned && <div className="flex items-center mb-1">
                            <FaParking className="mr-2 text-blue-500"/>
                            <span>{t("parking_assigned")}</span>
                        </div>
                        }
                        <div className="flex items-center mb-1">
                            <FaClock className="mr-2 text-gray-500"/>
                            <span>{reservation.periode === Periode.HeleDag ? t("whole_day") : t("part_day")}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
